import React from 'react';
import type { GetTicketStatusOutput } from '../../types/conciergeToolTypes';
import { SupportTicketCardChrome } from './SupportTicketCardChrome';
import type { SupportTicketCardStyles } from './supportTicketCardConfig';

/**
 * Renders a single ticket's status with the latest admin reply quoted as a
 * blockquote. Driven by the `tool-get_ticket_status` part.
 */
export function SupportTicketStatusPart({ output }: { output: GetTicketStatusOutput | undefined }) {
  if (!output || output.status !== 'ok' || !output.ticketId) return null;

  return (
    <SupportTicketCardChrome>
      {(styles) => <SupportTicketStatusView output={output} styles={styles} />}
    </SupportTicketCardChrome>
  );
}

function SupportTicketStatusView({
  output,
  styles,
}: {
  output: GetTicketStatusOutput;
  styles: SupportTicketCardStyles;
}) {
  const ref = output.displayId ?? output.ticketId;
  const state = output.state ? humanizeState(output.state) : 'Submitted';
  const reply = output.latestAdminReply;
  const title = output.title ?? output.ticketTypeName ?? `Ticket #${ref}`;

  return (
    <div
      className="tw:my-2 tw:border tw:shadow-sm"
      style={{
        borderRadius: styles.cardBorderRadius,
        borderColor: styles.cardBorderColor,
        backgroundColor: styles.cardBgColor,
        padding: styles.cardPadding,
      }}
      role="group"
      aria-label="Support ticket status"
    >
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <div className="tw:flex tw:items-center tw:gap-2">
            <span
              className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide"
              style={{ color: styles.labelColor }}
            >
              Ticket
            </span>
            <span
              className={`tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium ${stateClass(output.state)}`}
              style={isDefaultState(output.state) ? defaultStateStyle : undefined}
            >
              {state}
            </span>
          </div>
          <div
            className="tw:font-semibold"
            style={{ fontSize: styles.refFontSize, color: styles.refColor }}
          >
            #{ref}
          </div>
          <div style={{ fontSize: styles.metaFontSize, color: styles.metaColor }}>{title}</div>
        </div>
        {output.ticketUrl && (
          <a
            href={output.ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:border tw:px-3 tw:py-1.5 tw:font-semibold tw:no-underline"
            style={{
              borderRadius: styles.viewBorderRadius,
              borderColor: styles.viewBorderColor,
              backgroundColor: styles.viewBgColor,
              color: styles.viewTextColor,
              fontSize: styles.viewFontSize,
            }}
          >
            View
          </a>
        )}
      </div>

      {reply ? (
        <div
          className="tw:mt-4 tw:rounded-lg tw:border-l-4 tw:p-3"
          style={{ borderColor: styles.replyBorderColor, backgroundColor: styles.replyBgColor }}
        >
          <div className="tw:text-xs tw:font-semibold" style={{ color: styles.bodyColor }}>
            {reply.authorName ? `${reply.authorName} replied` : 'Latest reply from support'}
          </div>
          <div className="tw:mb-1" style={{ fontSize: styles.metaFontSize, color: styles.metaColor }}>
            {new Date(reply.postedAt).toLocaleString()}
          </div>
          <div
            className="tw:leading-relaxed tw:whitespace-pre-wrap"
            style={{ fontSize: styles.bodyFontSize, color: styles.replyTextColor }}
          >
            {stripHtml(reply.body)}
          </div>
        </div>
      ) : (
        <p
          className="tw:mt-3"
          style={{ fontSize: styles.bodyFontSize, color: styles.metaColor }}
        >
          No reply from the support team yet.
        </p>
      )}
    </div>
  );
}

function humanizeState(state?: string): string {
  switch (state) {
    case 'submitted':
      return 'Submitted';
    case 'in_progress':
      return 'In progress';
    case 'waiting_on_customer':
      return 'Waiting on you';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
    default:
      return (state ?? 'Submitted').replace(/_/g, ' ');
  }
}

function stateClass(state?: string): string {
  switch (state) {
    case 'resolved':
    case 'closed':
      return 'tw:bg-gray-100 tw:text-gray-600';
    case 'waiting_on_customer':
      return 'tw:bg-amber-50 tw:text-amber-700';
    case 'in_progress':
      return 'tw:bg-blue-50 tw:text-blue-700';
    default:
      // D-33 — see `defaultStateStyle` below.
      return '';
  }
}

// D-33 — open/submitted ticket pill picks up the brand's support accent.
function isDefaultState(state?: string): boolean {
  return state !== 'resolved' && state !== 'closed' && state !== 'waiting_on_customer' && state !== 'in_progress';
}

const defaultStateStyle: React.CSSProperties = {
  backgroundColor: 'var(--bc-support-bg)',
  color: 'var(--bc-support-text)',
};

function stripHtml(html: string): string {
  // Light-touch removal of obvious HTML tags and entities. Intercom sometimes
  // sends styled HTML; users want a clean readout.
  return html
    .replace(/<br\s*\/?>(\n)?/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim();
}
